/**
 * DeleteMilestoneModal — deleting a milestone so the schedule HEALS (docs/OPERATING_MODEL.md; Matt,
 * 2026-09-24). Replaces the outline's old "type DELETE" box, which only dropped the links and left
 * whatever came after waiting on nothing.
 *
 * Shows, before anything is deleted (rewritten in plain questions 2026-10-27 — Matt: "unintelligible"):
 *   1. One sentence: what is deleted, for good. The rest of its GROUP on this project, to delete too (unticked).
 *   2. For each milestone that waited on it, one question — "what should it wait for now?" — answered from
 *      cards that each say what happens: just what it already waits on (the default when there is one),
 *      what the deleted one waited for (the handover), a different milestone, or a fixed date (which won't
 *      move if earlier work slips).
 *   3. Type DELETE to confirm.
 *
 * Opens on `open-delete-ms-modal` { msId, projectId }. Mounted once in OutlineView.
 */
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { planMilestoneDelete, applyMilestoneDelete } from '../services/milestoneDelete.js'
import { ctx } from '../outline/ctx.js'
import { wf } from '../outline/state.js'
import { PALETTE } from './MiniChart.jsx'

export default function DeleteMilestoneModal() {
  const [target, setTarget] = useState(null)       // { msId, projectId }
  const [plan, setPlan] = useState(null)
  const [also, setAlso] = useState([])
  const [pick, setPick] = useState({})             // looseId -> { mode: 'link'|'date'|'none', predId, date }
  const [typed, setTyped] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  useEffect(() => {
    const onOpen = (e) => {
      const d = e.detail || {}
      if (!d.msId) return
      setTarget(d); setPlan(null); setAlso([]); setPick({}); setTyped(''); setErr(''); setBusy(false)
    }
    window.addEventListener('open-delete-ms-modal', onOpen)
    return () => window.removeEventListener('open-delete-ms-modal', onOpen)
  }, [])

  useEffect(() => {
    if (!target) return
    let alive = true
    setPlan(null)
    planMilestoneDelete(target.msId, also).then(p => {
      if (!alive) return
      setPlan(p)
      // Default each loose milestone to the suggested handover (or to "nothing" when there is none).
      setPick(prev => {
        const next = {}
        // A pick made before more milestones were ticked for deletion may point at one now being deleted
        // (2026-10-27: the dropdown came up empty on Patch's hearing chain): keep it only while it is still
        // a real choice, else fall back to the new suggestion.
        const valid = new Set((p.choices || []).map(x => x.id))
        for (const l of p.loose) {
          const old = prev[l.id]
          const okOld = old && (old.mode !== 'link' || (old.predId && valid.has(old.predId)))
          // Still waits on something else → leave it at that (2026-10-27, Matt couldn't read the old default).
          next[l.id] = okOld ? old : (l.keeps.length ? { mode: 'none', predId: '' } : l.suggested.length ? { mode: 'link', predId: l.suggested[0] } : { mode: 'link', predId: '' })
        }
        return next
      })
    }).catch(e => { if (alive) setErr(String(e && e.message || e)) })
    return () => { alive = false }
  }, [target, also])

  useEffect(() => {
    if (!target) return
    const onKey = (e) => { if (e.key === 'Escape' && !busy) setTarget(null) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [target, busy])

  if (!target) return null
  const close = () => { if (!busy) setTarget(null) }

  async function confirm() {
    if (typed.trim() !== 'DELETE' || !plan) return
    for (const l of plan.loose) {
      const c = pick[l.id] || {}
      if (c.mode === 'date' && !c.date) { setErr('Pick the fixed start date for ' + l.name + ', or choose a milestone for it to wait on.'); return }
    }
    setBusy(true); setErr('')
    const relinks = plan.loose.map(l => {
      const c = pick[l.id] || {}
      if (c.mode === 'date') return { id: l.id, fixedStart: c.date }
      if (c.mode === 'link' && c.predId) return { id: l.id, predecessorIds: [c.predId] }
      return { id: l.id, predecessorIds: [] }
    })
    try {
      const r = await applyMilestoneDelete(target.msId, also, relinks)
      if (r.deleted && wf.zoomMsId && r.deleted.includes(wf.zoomMsId)) { wf.zoomMsId = null; wf.zoomCat = null; wf.zoomItemId = null; wf.zoomSection = null }
      const pid = target.projectId || plan.projectId
      if (ctx.reloadProjectData && pid) ctx.reloadProjectData(pid)
      window.dispatchEvent(new Event('outline-render'))
      setTarget(null)
    } catch (e) { setErr(String(e && e.message || e)); setBusy(false) }
  }

  const choiceLabel = (c) => c.name + (c.posted ? ' (done)' : '') + (c.phaseName ? ' · ' + c.phaseName.replace(/ Phase$/, '') : '')

  return createPortal(
    <div style={S.scrim} onClick={close}>
      <div style={S.card} onClick={e => e.stopPropagation()}>
        <div style={S.head}>
          <div style={S.title}>Delete milestone</div>
          <button style={S.x} onClick={close} disabled={busy}>×</button>
        </div>
        {!plan && !err && <div style={S.muted}>Working out what this changes…</div>}
        {err && <div style={{ color: PALETTE.red, fontSize: 13, margin: '6px 0' }}>{err}</div>}
        {plan && <>

          <div style={S.lead}>
            {plan.deleting.length === 1
              ? <>Delete <b>{plan.deleting[0].name}</b>?</>
              : <>Delete <b>{plan.deleting.length} milestones</b>: {plan.deleting.map(d => d.name).join(', ')}?</>}
            {' '}It and its checklist are removed for good.
          </div>

          {plan.group.members.length > 0 && <>
            <div style={S.q}>It belongs with these. Delete them too?</div>
            {plan.group.members.map(m => (
              <label key={m.id} style={S.check}>
                <input type="checkbox" checked={also.includes(m.id)} disabled={busy}
                  onChange={e => setAlso(a => e.target.checked ? [...a, m.id] : a.filter(x => x !== m.id))} />
                {m.name}
              </label>
            ))}
          </>}

          {plan.loose.length === 0 && <div style={S.note}>Nothing waits on it, so no other dates change.</div>}
          {/* One plain question per milestone that waited on the deleted one (Matt, 2026-10-27: the old
              "it waited on / waits on it now" map was "unintelligible"). Each answer says what happens. */}
          {plan.loose.map(l => {
            const c = pick[l.id] || { mode: 'link', predId: '' }
            const set = (patch) => setPick(p => ({ ...p, [l.id]: { ...c, ...patch } }))
            const byId = Object.fromEntries((plan.choices || []).map(x => [x.id, x]))
            const sugg = l.suggested.map(id => byId[id]).filter(Boolean)
            const isDone = (k) => !!(k.posted || (byId[k.id] && byId[k.id].posted))
            const keepsTxt = l.keeps.map(k => k.name + (isDone(k) ? ' (done)' : '')).join(' and ')
            const keepsDone = l.keeps.length > 0 && l.keeps.every(isDone)
            const otherId = c.mode === 'link' && c.predId && !l.suggested.includes(c.predId) ? c.predId : ''
            const opt = (on, onPick, title, sub) => (
              <label style={{ ...S.choice, ...(on ? S.choiceOn : null) }}>
                <input type="radio" checked={on} disabled={busy} onChange={onPick} style={{ marginTop: 3 }} />
                <span><span style={S.choiceT}>{title}</span>{sub && <span style={S.choiceS}>{sub}</span>}</span>
              </label>
            )
            return (
              <div key={l.id} style={S.block}>
                <div style={S.q}><b>{l.name}</b> was waiting on this. What should it wait for now?</div>
                {l.keeps.length > 0 && opt(c.mode === 'none', () => set({ mode: 'none' }),
                  `Just ${keepsTxt}`,
                  keepsDone ? 'That is already done, so it can start now.' : 'It starts once that is done.')}
                {sugg.map(x => opt(c.mode === 'link' && c.predId === x.id, () => set({ mode: 'link', predId: x.id }),
                  (l.keeps.length ? 'Also ' : '') + x.name + (x.posted ? ' (done)' : ''),
                  `What ${plan.deleting[0].name} was waiting for, passed on to it.`))}
                {opt(c.mode === 'link' && !l.suggested.includes(c.predId), () => set({ mode: 'link', predId: otherId }),
                  (l.keeps.length ? 'Also a' : 'A') + ' different milestone',
                  <select style={S.select} disabled={busy} value={otherId} onClick={e => e.stopPropagation()} onChange={e => set({ mode: 'link', predId: e.target.value })}>
                    <option value="">Choose one…</option>
                    {plan.choices.filter(x => x.id !== l.id).map(x => <option key={x.id} value={x.id}>{choiceLabel(x)}</option>)}
                  </select>)}
                {opt(c.mode === 'date', () => set({ mode: 'date' }),
                  'A fixed start date',
                  <><input type="date" style={S.select} disabled={busy} value={c.date || ''} onClick={e => e.stopPropagation()} onChange={e => set({ mode: 'date', date: e.target.value })} /> <span style={S.caution}>It won't move if earlier work slips.</span></>)}
                {c.mode === 'link' && !c.predId && l.keeps.length === 0 && <div style={S.caution}>Pick a milestone, or it will wait on nothing and look ready to start.</div>}
              </div>
            )
          })}
          {plan.loose.length > 0 && <div style={S.note}>The dates after it adjust to match.</div>}

          <div style={S.label}>Type DELETE to confirm</div>
          <input style={S.input} value={typed} placeholder="DELETE" disabled={busy} onChange={e => setTyped(e.target.value)} />
          <div style={S.footer}>
            <button style={S.cancel} onClick={close} disabled={busy}>Cancel</button>
            <button style={{ ...S.danger, opacity: typed.trim() === 'DELETE' && !busy ? 1 : 0.4 }} disabled={typed.trim() !== 'DELETE' || busy} onClick={confirm}>
              {busy ? 'Deleting…' : `Delete${plan.deleting.length > 1 ? ' ' + plan.deleting.length + ' milestones' : ''}`}
            </button>
          </div>
        </>}
      </div>
    </div>,
    document.body
  )
}

const S = {
  scrim: { position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  card: { background: 'var(--aj-bg-elevated)', borderRadius: 10, width: 'min(620px, 96vw)', maxHeight: '90vh', overflowY: 'auto', padding: '16px 20px', boxShadow: '0 18px 60px rgba(0,0,0,0.28)', fontFamily: 'DM Sans, sans-serif', color: 'var(--aj-text)' },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },
  title: { fontSize: 16, fontWeight: 600 },
  x: { background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: 'var(--aj-text-mid)' },
  lead: { fontSize: 14, lineHeight: 1.5, margin: '2px 0 4px' },
  q: { fontSize: 13.5, margin: '14px 0 6px' },
  note: { fontSize: 12.5, color: 'var(--aj-text-mid)', marginTop: 10 },
  block: { borderTop: '0.5px solid var(--aj-border)', marginTop: 10 },
  choice: { display: 'flex', gap: 8, alignItems: 'flex-start', padding: '8px 10px', border: '0.5px solid var(--aj-border)', borderRadius: 8, marginBottom: 6, cursor: 'pointer', background: 'var(--aj-bg-surface)' },
  choiceOn: { borderColor: 'var(--aj-accent, #07272D)', boxShadow: '0 0 0 1px var(--aj-accent, #07272D) inset' },
  choiceT: { display: 'block', fontSize: 13, fontWeight: 600 },
  choiceS: { display: 'block', fontSize: 12, color: 'var(--aj-text-mid)', marginTop: 3 },
  warn: { background: 'rgba(192,57,43,0.06)', border: '0.5px solid rgba(192,57,43,0.25)', borderRadius: 6, padding: '9px 12px', fontSize: 12.5, color: 'var(--aj-text)', marginBottom: 8 },
  label: { fontFamily: 'DM Mono, monospace', fontSize: 10.5, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', margin: '14px 0 4px' },
  gone: { fontSize: 13.5, fontWeight: 600, textDecoration: 'line-through', color: PALETTE.red },
  goneInline: { textDecoration: 'line-through', color: PALETTE.red },
  check: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, padding: '2px 0' },
  map: { border: '0.5px solid var(--aj-border)', borderRadius: 8, padding: '8px 12px', background: 'var(--aj-bg-surface)' },
  mapRow: { display: 'flex', gap: 12, alignItems: 'baseline', padding: '5px 0', fontSize: 13, borderTop: '0.5px solid var(--aj-border)' },
  mapKey: { width: 92, flexShrink: 0, fontFamily: 'DM Mono, monospace', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--aj-text-muted)' },
  loose: { padding: '4px 0 8px' },
  opts: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, flexWrap: 'wrap' },
  opt: { display: 'flex', alignItems: 'center', gap: 5, fontSize: 12.5, minWidth: 140 },
  select: { font: 'inherit', fontSize: 12.5, padding: '3px 6px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-surface)', color: 'var(--aj-text)', maxWidth: 320 },
  caution: { fontSize: 11.5, color: '#b9770e' },
  muted: { fontSize: 12, color: 'var(--aj-text-muted)' },
  input: { width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, fontSize: 14, letterSpacing: '.05em', background: 'var(--aj-bg-surface)', color: 'var(--aj-text)' },
  footer: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 },
  cancel: { fontFamily: 'DM Sans, sans-serif', fontSize: 13, padding: '6px 14px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', background: 'transparent', color: 'var(--aj-text)', cursor: 'pointer' },
  danger: { fontFamily: 'DM Sans, sans-serif', fontSize: 13, fontWeight: 600, padding: '6px 16px', borderRadius: 4, border: 'none', background: '#c0392b', color: '#fff', cursor: 'pointer' },
}
